// @ts-strict-ignore
import * as React from 'react';
import Space from 'antd/lib/space';
import Tooltip from 'antd/lib/tooltip';
import Text from 'antd/lib/typography/Text';
import { CheckCircleFilled, WarningFilled } from '@ant-design/icons';
import {
    AppleAppStoreDefaultSettings,
    AppPlatform,
    AppStoreType,
    AutomationAutoStagedRollout,
    GoogleAppStoreSettings,
    GooglePlayStoreData,
    HuaweiAppGallerySettings,
    ReleaseStepSubmissionData,
    AppleAppStoreData,
    HuaweiAppStoreData,
    ReviewAttachment,
    ReviewAttachmentUploadStatus,
    ReleaseStepType,
} from '@/types';
import { antGrey, runwayColors } from '@/components/styles/globalStyles';
import {
    ArchetypeSpecificStepRunSummaryPanelProps,
    StepRunSummaryContainer,
    StepRunSummaryDetailItem,
    StepRunSummaryDetailSeparator,
    AppReviewInformationDetailItem,
    useSRRTopBarData,
    createSRRTopBar,
} from '.';
import {
    appleReleaseTypeForReleaseMethod,
    getRolloutDaysToPercentageFromAutomation,
    releaseSettingsNeedPhasedReleaseWarning,
} from '@/helpers';
import { labelForInAppUpdatePriority } from '@/helpers/appSettingsHelpers';
import {
    convertAndroidStagedRolloutPercentage,
    displayStringForRolloutPercentage,
    displayVersionReleaseType,
} from '@/helpers/displayHelpers';
import { Nullable } from '@/typescriptHelpers';
import { RootState } from '@/reducers/reducers';
import { selectAutomationById } from '@/selectors/selectors';
import { useSelector } from 'react-redux';
import TooltipInfoIcon from '../../helperComponents/TooltipInfoIcon';

interface AppleAppStoreSubmissionDetailsProps {
    appStoreData: Nullable<AppleAppStoreData>;
    appStoreReleaseSettings: Nullable<AppleAppStoreDefaultSettings>;
    platform: AppPlatform;
    isRollback: boolean;
    appDefaultSettings: Nullable<AppleAppStoreDefaultSettings>;
    reviewAttachments: Nullable<ReviewAttachment[]>;
    shouldDisplayCompactVersion: boolean;
}

const AppleAppStoreSubmissionDetails: React.FC<AppleAppStoreSubmissionDetailsProps> = ({
    appStoreData,
    appStoreReleaseSettings,
    platform,
    isRollback,
    appDefaultSettings,
    reviewAttachments,
    shouldDisplayCompactVersion,
}) => {
    const isPhasedRelease = appStoreData?.phasedRelease ? true : (appStoreReleaseSettings?.phasedRelease ?? false);
    const releaseType =
        appStoreData?.versionReviewSubmissionItem?.releaseType ??
        (appStoreReleaseSettings?.releaseMethod ?
            appleReleaseTypeForReleaseMethod(appStoreReleaseSettings.releaseMethod)
        :   undefined);
    const earliestReleaseDate = appStoreData?.versionReviewSubmissionItem?.earliestReleaseDate ?? undefined;

    const phasedReleaseText =
        isPhasedRelease ? 'On (Release will be phased over 7 days)' : 'Off (Release update to all users immediately)';

    const needsPhasedReleaseWarning = releaseSettingsNeedPhasedReleaseWarning(
        platform,
        isRollback,
        appDefaultSettings,
        appStoreReleaseSettings,
    );

    const versionReleaseText = displayVersionReleaseType(releaseType, earliestReleaseDate);

    const usesNonExemptEncryption = appStoreReleaseSettings?.exportComplianceInfoUsesEncryption ?? false;
    const encryptionText = `Uses non-exempt encryption: ${usesNonExemptEncryption ? 'Yes' : 'No'}`;
    const encryptionTooltip =
        'To bypass setting up export compliance in App Store Connect, you can specify your use of encryption directly in the information property list (Info.plist) in your Xcode project. If you need to provide documentation, Apple will provide you with a key value to add to the Info.plist.';

    const reviewDetail = appStoreData?.reviewDetail;
    const reviewNotes = reviewDetail?.notes;
    const completedAttachments = reviewAttachments?.filter(
        (attachment) => attachment.status === ReviewAttachmentUploadStatus.Complete,
    );

    return (
        <>
            <StepRunSummaryDetailItem header="VERSION RELEASE" body={versionReleaseText} />
            <StepRunSummaryDetailSeparator />
            <StepRunSummaryDetailItem
                header="PHASED RELEASE"
                body={
                    <Space align="center" size={8}>
                        <Text style={{ color: antGrey.grey8 }}>{phasedReleaseText}</Text>
                        {needsPhasedReleaseWarning && (
                            <Tooltip title="Phased release is off for this release because it's a rollback.">
                                <WarningFilled
                                    style={{ color: runwayColors.statusYellow, fontSize: 14, cursor: 'pointer' }}
                                />
                            </Tooltip>
                        )}
                    </Space>
                }
            />
            {!shouldDisplayCompactVersion && (
                <>
                    <StepRunSummaryDetailSeparator />
                    <StepRunSummaryDetailItem
                        header="EXPORT COMPLIANCE"
                        body={
                            <Space align="center" size={8}>
                                <Text style={{ color: antGrey.grey8 }}>{encryptionText}</Text>
                                <TooltipInfoIcon
                                    tooltipContent={encryptionTooltip}
                                    style={{ fontSize: 14, cursor: 'pointer' }}
                                />
                            </Space>
                        }
                    />

                    <div style={{ width: '100%', height: 1, backgroundColor: antGrey.grey4 }} />
                    <AppReviewInformationDetailItem
                        reviewNotes={reviewNotes}
                        attachments={completedAttachments}
                        contactFirstName={reviewDetail?.contactFirstName}
                        contactLastName={reviewDetail?.contactLastName}
                        contactEmail={reviewDetail?.contactEmail}
                        contactPhone={reviewDetail?.contactPhone}
                        demoAccountRequired={reviewDetail?.demoAccountRequired}
                    />
                </>
            )}
        </>
    );
};

interface GooglePlayStoreSubmissionDetailsProps {
    googleStoreData: Nullable<GooglePlayStoreData>;
    releaseSettings: Nullable<GoogleAppStoreSettings>;
    appDefaultSettings: Nullable<GoogleAppStoreSettings>;
    platform: AppPlatform;
    isRollback: boolean;
    customStagedRolloutEnabled: boolean;
    stagedRolloutSequence: Nullable<number[]>;
    shouldDisplayCompactVersion: boolean;
}

const GooglePlayStoreSubmissionDetails: React.FC<GooglePlayStoreSubmissionDetailsProps> = ({
    releaseSettings,
    appDefaultSettings,
    platform,
    isRollback,
    customStagedRolloutEnabled,
    stagedRolloutSequence,
    shouldDisplayCompactVersion,
}) => {
    const stagedRolloutEnabled = !!releaseSettings?.stagedRelease;
    const stagedRolloutEnabledText =
        releaseSettings ?
            releaseSettings.stagedRelease ?
                'Yes'
            :   'No'
        :   'Not set';

    const stagedRolloutPercent =
        releaseSettings?.initialStagedRolloutPercentage ?
            displayStringForRolloutPercentage(
                convertAndroidStagedRolloutPercentage(releaseSettings.initialStagedRolloutPercentage),
            )
        :   null;

    const customStagedRolloutActive = customStagedRolloutEnabled && !isRollback;

    const showStagedRolloutPercent = stagedRolloutEnabled && !customStagedRolloutActive;

    const customStagedRolloutAutomationEnabledButStagedRolloutSettingDisabled =
        customStagedRolloutActive && !stagedRolloutEnabled;

    const needsSettingsWarning = customStagedRolloutAutomationEnabledButStagedRolloutSettingDisabled;
    const settingsWarningText =
        "Runway is unable to perform your configured custom staged rollout automation because staged rollout is not enabled. Change this default release method to staged rollout 'Yes' to allow Runway to perform your custom staged rollout.";

    const needsSettingsConfirmation = stagedRolloutEnabled && customStagedRolloutActive;
    const confirmationText =
        "Keep your staged rollout set to 'Yes' to allow Runway to proceed with your custom staged rollout automation.";

    const needsPhasedReleaseWarning = releaseSettingsNeedPhasedReleaseWarning(
        platform,
        isRollback,
        appDefaultSettings,
        releaseSettings,
    );

    const autoStagedRolloutSequence: string[] = [];
    if (stagedRolloutSequence) {
        stagedRolloutSequence.forEach((percent, i) => {
            autoStagedRolloutSequence.push(`Day ${i + 1}: ${percent}%`);
        });
    }

    const showCustomStagedRollout = stagedRolloutEnabled;

    const getStagedRolloutEnabledBody = () => {
        const hasWarningOrConfirmation = needsSettingsWarning || needsSettingsConfirmation || needsPhasedReleaseWarning;

        if (!hasWarningOrConfirmation) {
            return <Text style={{ color: antGrey.grey8 }}>{stagedRolloutEnabledText}</Text>;
        }

        const tooltipMessages: { icon: 'warning' | 'check'; message: string }[] = [];

        if (needsSettingsWarning) {
            tooltipMessages.push({ icon: 'warning', message: settingsWarningText });
        }
        if (needsSettingsConfirmation) {
            tooltipMessages.push({ icon: 'check', message: confirmationText });
        }
        if (needsPhasedReleaseWarning) {
            tooltipMessages.push({
                icon: 'warning',
                message: "Staged rollout has been disabled for this release because it's a rollback.",
            });
        }

        const tooltipContent = (
            <Space direction="vertical" size={4}>
                {tooltipMessages.map((msg, idx) => (
                    <Space key={idx} align="start">
                        {msg.icon === 'warning' ?
                            <WarningFilled style={{ color: runwayColors.statusYellow }} />
                        :   <CheckCircleFilled style={{ color: runwayColors.statusGreen }} />}
                        <Text style={{ color: 'white' }}>{msg.message}</Text>
                    </Space>
                ))}
            </Space>
        );

        const hasWarning = needsSettingsWarning || needsPhasedReleaseWarning;

        return (
            <Space align="center" size={8}>
                <Text style={{ color: antGrey.grey8 }}>{stagedRolloutEnabledText}</Text>
                <Tooltip title={tooltipContent}>
                    {hasWarning ?
                        <WarningFilled style={{ color: runwayColors.statusYellow, fontSize: 14, cursor: 'pointer' }} />
                    :   <CheckCircleFilled
                            style={{ color: runwayColors.statusGreen, fontSize: 14, cursor: 'pointer' }}
                        />
                    }
                </Tooltip>
            </Space>
        );
    };

    const getCustomStagedRolloutBody = () => {
        if (!customStagedRolloutActive) {
            return <Text style={{ color: antGrey.grey8 }}>No</Text>;
        }

        if (stagedRolloutSequence && stagedRolloutSequence.length > 0) {
            const tooltipContent = (
                <Space direction="vertical">
                    <Space direction="vertical" size={0}>
                        {autoStagedRolloutSequence.map((day, i) => (
                            <Text style={{ color: 'white' }} key={i}>
                                {day}
                            </Text>
                        ))}
                    </Space>
                    <Text style={{ color: 'white' }}>
                        You can configure your custom staged rollout from automation settings.
                    </Text>
                </Space>
            );

            return (
                <Space align="center" size={8}>
                    <Text style={{ color: antGrey.grey8 }}>Yes</Text>
                    <TooltipInfoIcon tooltipContent={tooltipContent} style={{ fontSize: 14, cursor: 'pointer' }} />
                </Space>
            );
        }

        // Custom staged rollout is enabled but we don't have the sequence details
        return <Text style={{ color: antGrey.grey8 }}>Yes</Text>;
    };

    return (
        <>
            <StepRunSummaryDetailItem header="STAGED ROLLOUT ENABLED" body={getStagedRolloutEnabledBody()} />
            {showCustomStagedRollout && (
                <>
                    <StepRunSummaryDetailSeparator />
                    <StepRunSummaryDetailItem header="CUSTOM STAGED ROLLOUT" body={getCustomStagedRolloutBody()} />
                </>
            )}
            {showStagedRolloutPercent && (
                <>
                    <StepRunSummaryDetailSeparator />
                    <StepRunSummaryDetailItem
                        header="INITIAL ROLLOUT PERCENTAGE"
                        body={stagedRolloutPercent ?? 'Not set'}
                    />
                </>
            )}
            {!shouldDisplayCompactVersion && (
                <>
                    <StepRunSummaryDetailSeparator />
                    <StepRunSummaryDetailItem
                        header="IN-APP UPDATE PRIORITY"
                        body={labelForInAppUpdatePriority(releaseSettings?.inAppUpdatePriority)}
                    />
                </>
            )}
        </>
    );
};

interface HuaweiAppStoreSubmissionDetailsProps {
    huaweiData: Nullable<HuaweiAppStoreData>;
    releaseSettings: Nullable<HuaweiAppGallerySettings>;
    appDefaultSettings: Nullable<HuaweiAppGallerySettings>;
    customPhasedReleaseEnabled: boolean;
    phasedReleaseSequence: Nullable<number[]>;
    shouldDisplayCompactVersion: boolean;
}

const HuaweiAppStoreSubmissionDetails: React.FC<HuaweiAppStoreSubmissionDetailsProps> = ({
    releaseSettings,
    customPhasedReleaseEnabled,
    phasedReleaseSequence,
}) => {
    const phasedReleaseEnabled = !!releaseSettings?.stagedRelease;
    const phasedReleaseEnabledText =
        releaseSettings ?
            releaseSettings.stagedRelease ?
                'Yes'
            :   'No'
        :   'Not set';

    const phasedReleasePercent =
        releaseSettings?.initialStagedRolloutPercentage ?
            displayStringForRolloutPercentage(
                convertAndroidStagedRolloutPercentage(releaseSettings.initialStagedRolloutPercentage),
            )
        :   null;

    const customPhasedReleaseActive = customPhasedReleaseEnabled;
    const showPhasedReleasePercent = phasedReleaseEnabled && !customPhasedReleaseActive;

    const customPhasedReleaseAutomationEnabledButPhasedReleaseSettingDisabled =
        customPhasedReleaseActive && !phasedReleaseEnabled;

    const needsSettingsWarning = customPhasedReleaseAutomationEnabledButPhasedReleaseSettingDisabled;
    const settingsWarningText =
        "Runway is unable to perform your configured custom phased release automation because phased release is not enabled. Change this default release method to phased release 'Yes' to allow Runway to perform your custom phased release.";

    const needsSettingsConfirmation = phasedReleaseEnabled && customPhasedReleaseActive;
    const confirmationText =
        "Keep your phased release set to 'Yes' to allow Runway to proceed with your custom phased release automation.";

    const autoPhasedReleaseSequence: string[] = [];
    if (phasedReleaseSequence) {
        phasedReleaseSequence.forEach((percent, i) => {
            autoPhasedReleaseSequence.push(`Day ${i + 1}: ${percent}%`);
        });
    }

    const showCustomPhasedRelease = phasedReleaseEnabled;

    const getPhasedReleaseEnabledBody = () => {
        const hasWarningOrConfirmation = needsSettingsWarning || needsSettingsConfirmation;

        if (!hasWarningOrConfirmation) {
            return <Text style={{ color: antGrey.grey8 }}>{phasedReleaseEnabledText}</Text>;
        }

        const tooltipMessages: { icon: 'warning' | 'check'; message: string }[] = [];

        if (needsSettingsWarning) {
            tooltipMessages.push({ icon: 'warning', message: settingsWarningText });
        }
        if (needsSettingsConfirmation) {
            tooltipMessages.push({ icon: 'check', message: confirmationText });
        }

        const tooltipContent = (
            <Space direction="vertical" size={4}>
                {tooltipMessages.map((msg, idx) => (
                    <Space key={idx} align="start">
                        {msg.icon === 'warning' ?
                            <WarningFilled style={{ color: runwayColors.statusYellow }} />
                        :   <CheckCircleFilled style={{ color: runwayColors.statusGreen }} />}
                        <Text style={{ color: 'white' }}>{msg.message}</Text>
                    </Space>
                ))}
            </Space>
        );

        const hasWarning = needsSettingsWarning;

        return (
            <Space align="center" size={8}>
                <Text style={{ color: antGrey.grey8 }}>{phasedReleaseEnabledText}</Text>
                <Tooltip title={tooltipContent}>
                    {hasWarning ?
                        <WarningFilled style={{ color: runwayColors.statusYellow, fontSize: 14, cursor: 'pointer' }} />
                    :   <CheckCircleFilled
                            style={{ color: runwayColors.statusGreen, fontSize: 14, cursor: 'pointer' }}
                        />
                    }
                </Tooltip>
            </Space>
        );
    };

    const getCustomPhasedReleaseBody = () => {
        if (!customPhasedReleaseActive) {
            return <Text style={{ color: antGrey.grey8 }}>No</Text>;
        }

        if (phasedReleaseSequence && phasedReleaseSequence.length > 0) {
            const tooltipContent = (
                <Space direction="vertical">
                    <Space direction="vertical" size={0}>
                        {autoPhasedReleaseSequence.map((day, i) => (
                            <Text style={{ color: 'white' }} key={i}>
                                {day}
                            </Text>
                        ))}
                    </Space>
                    <Text style={{ color: 'white' }}>
                        You can configure your custom phased release from automation settings.
                    </Text>
                </Space>
            );

            return (
                <Space align="center" size={8}>
                    <Text style={{ color: antGrey.grey8 }}>Yes</Text>
                    <TooltipInfoIcon tooltipContent={tooltipContent} style={{ fontSize: 14, cursor: 'pointer' }} />
                </Space>
            );
        }

        return <Text style={{ color: antGrey.grey8 }}>Yes</Text>;
    };

    return (
        <>
            <StepRunSummaryDetailItem header="PHASED RELEASE ENABLED" body={getPhasedReleaseEnabledBody()} />
            {showCustomPhasedRelease && (
                <>
                    <StepRunSummaryDetailSeparator />
                    <StepRunSummaryDetailItem header="CUSTOM PHASED RELEASE" body={getCustomPhasedReleaseBody()} />
                </>
            )}
            {showPhasedReleasePercent && (
                <>
                    <StepRunSummaryDetailSeparator />
                    <StepRunSummaryDetailItem
                        header="INITIAL PHASED RELEASE PERCENTAGE"
                        body={phasedReleasePercent ?? 'Not set'}
                    />
                </>
            )}
        </>
    );
};

interface SubmissionDetailsFactoryParams {
    appStoreType: AppStoreType;
    stepData: ReleaseStepSubmissionData;
    isRollback: boolean;
    stagedRolloutSequence: Nullable<number[]>;
    reviewAttachments: Nullable<ReviewAttachment[]>;
    shouldDisplayCompactVersion: boolean;
}

const getSubmissionDetailsByAppStoreType = ({
    appStoreType,
    stepData,
    isRollback,
    stagedRolloutSequence,
    reviewAttachments,
    shouldDisplayCompactVersion,
}: SubmissionDetailsFactoryParams): React.ReactNode => {
    switch (appStoreType) {
        case AppStoreType.Apple:
            return (
                <AppleAppStoreSubmissionDetails
                    appStoreData={stepData.appStoreData as AppleAppStoreData}
                    appStoreReleaseSettings={stepData.appStoreReleaseSettings as AppleAppStoreDefaultSettings}
                    platform={stepData.platform}
                    appDefaultSettings={stepData.appStoreReleaseSettings as AppleAppStoreDefaultSettings}
                    isRollback={isRollback}
                    reviewAttachments={reviewAttachments}
                    shouldDisplayCompactVersion={shouldDisplayCompactVersion}
                />
            );
        case AppStoreType.Google:
            return (
                <GooglePlayStoreSubmissionDetails
                    googleStoreData={stepData.appStoreData as GooglePlayStoreData}
                    releaseSettings={stepData.appStoreReleaseSettings as GoogleAppStoreSettings}
                    appDefaultSettings={stepData.appStoreReleaseSettings as GoogleAppStoreSettings}
                    platform={stepData.platform}
                    isRollback={isRollback}
                    customStagedRolloutEnabled={!!stepData.autoStagedRolloutAutomationId}
                    stagedRolloutSequence={stagedRolloutSequence}
                    shouldDisplayCompactVersion={shouldDisplayCompactVersion}
                />
            );
        case AppStoreType.Huawei:
            return (
                <HuaweiAppStoreSubmissionDetails
                    huaweiData={stepData.appStoreData as HuaweiAppStoreData}
                    releaseSettings={stepData.appStoreReleaseSettings as HuaweiAppGallerySettings}
                    appDefaultSettings={stepData.appStoreReleaseSettings as HuaweiAppGallerySettings}
                    customPhasedReleaseEnabled={!!stepData.autoStagedRolloutAutomationId}
                    phasedReleaseSequence={stagedRolloutSequence}
                    shouldDisplayCompactVersion={shouldDisplayCompactVersion}
                />
            );
        default:
            return null;
    }
};

export const SubmissionStepRunSummaryPanel: React.FC<ArchetypeSpecificStepRunSummaryPanelProps> = ({
    stepRun,
    appId,
    shouldDisplayCompactVersion,
}) => {
    const { stepData, appStoreData, isSubmitted, appStoreState, hasSelectedBuild } = useSRRTopBarData(stepRun);
    const isRollback = stepRun.type === ReleaseStepType.RollbackBuild;

    const submissionData = stepData as ReleaseStepSubmissionData;

    const autoStagedRolloutAutomationId = submissionData.autoStagedRolloutAutomationId;

    const autoStagedRolloutAutomation = useSelector((state: RootState) =>
        selectAutomationById(state, appId, autoStagedRolloutAutomationId),
    ) as AutomationAutoStagedRollout;
    const stagedRolloutSequence = getRolloutDaysToPercentageFromAutomation(autoStagedRolloutAutomation);

    return (
        <div>
            <StepRunSummaryContainer
                topBar={createSRRTopBar(
                    stepRun,
                    submissionData,
                    appStoreData,
                    isSubmitted,
                    appStoreState,
                    hasSelectedBuild,
                )}
            >
                {getSubmissionDetailsByAppStoreType({
                    appStoreType: submissionData.appStoreType,
                    stepData: submissionData,
                    isRollback,
                    stagedRolloutSequence,
                    reviewAttachments: appStoreData?.reviewDetail?.reviewAttachments,
                    shouldDisplayCompactVersion,
                })}
            </StepRunSummaryContainer>
        </div>
    );
};
